import { forwardRef, useRef } from 'react';
import FocusLock from 'react-focus-lock';
import cx from 'classnames';
import { useSingleAndDoubleClick } from '../../../hooks/useSingleAndDoubleClick';
import { renderInlineCssVars, useDesignTokens } from '../../../theme';
import { markEvent } from '../../../util/events';
import { getAllFocusable } from '../../../util/focus';
import { setRef } from '../../../util/ref-util';
import { useTilesContext } from '../Tile';
import cn from './TileContainer.css.json';
import './TileContainer.css';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { jsx as _jsx } from "react/jsx-runtime";
export const TileContainer = /*#__PURE__*/forwardRef(function TileContainer(props, ref) {
  const dt = useDesignTokens();
  // Feature flag for 'New Token Values'
  const isNewTokenValueFlagEnabled = useFeatureFlag('NOVACOMMONS-22288_ThemeModernization_NewTokenValues');
  const tileRef = useRef(null);
  const tilesContext = useTilesContext();
  const tileContextRef = props.index !== undefined ? tilesContext?.createTileRef?.(props.index) : null;
  const isGridCell = props.isGridCell ?? tilesContext?.isGridCell;
  const onSingleClick = e => {
    const selection = window.getSelection()?.toString() ?? '';
    // Prevent tile selection if click resulted in text selection.
    if (e.isDefaultPrevented() || selection.length > 0) return;

    // Prevent tile selection if the element clicked is focusable content.
    const tileRefElement = tileRef?.current ?? undefined;
    const focusableElements = getAllFocusable(tileRefElement);
    const clickedElementFocusable = focusableElements.includes(e.target);
    if (clickedElementFocusable) return;
    props.onSelect?.({
      selectionArea: 'tile',
      index: props.index,
      altKey: e.altKey,
      ctlKey: e.ctrlKey,
      metaKey: e.metaKey,
      shiftKey: e.shiftKey
    });
  };
  const onDoubleClick = e => {
    e.preventDefault();
    props.onOpen?.();
  };
  const clickHandlers = useSingleAndDoubleClick({
    persist: true,
    onSingleClick,
    onDoubleClick
  });
  const getTileRole = () => {
    // if this tile is inside a grid, the role has to be 'gridcell'
    if (isGridCell) return 'gridcell';
    // otherwise, honor any role passed in
    if (props.role) return props.role;
    // default to 'group'
    return 'group';
  };
  return /*#__PURE__*/_jsx("div", {
    style: {
      ...renderInlineCssVars(getTileTokens(dt, props, isNewTokenValueFlagEnabled), 'tile'),
      ...props.style
    },
    onContextMenu: props.onContextMenu,
    ref: element => {
      setRef(ref, element);
      setRef(tileRef, element);
      setRef(tileContextRef, element);
    },
    className: cx(cn.tile, props.className, props.selected && cn.selected),
    role: getTileRole(),
    "aria-label": props.ariaLabel,
    "aria-labelledby": props.ariaLabelledBy,
    "aria-describedby": props.ariaDescribedBy,
    "aria-selected": props.ariaSelected,
    tabIndex: props.tabIndex,
    "data-testid": props.testId,
    onClick: e => {
      props.onClick?.();
      (props.disableClickDelay ? onSingleClick : clickHandlers.onClick)(e);
    },
    onDoubleClick: props.disableClickDelay ? onDoubleClick : clickHandlers.onDoubleClick,
    onFocus: props.onFocus,
    onBlur: props.onBlur,
    onKeyDown: e => {
      props.onKeyDown?.(e);

      // Early return if the event is fired on the controls inside of the tile
      if (e.target !== e.currentTarget) return;
      if (e.key === 'Enter' && props.onOpen) {
        props.onOpen();
        markEvent(e); // To prevent the default button in Dialog from being activated
        e.preventDefault();
      } else if (e.key === ' ' && props.onSelect) {
        props.onSelect({
          index: props.index ?? undefined,
          selectionArea: 'tile',
          altKey: e.altKey,
          ctlKey: e.ctrlKey,
          metaKey: e.metaKey,
          shiftKey: e.shiftKey
        });
        e.preventDefault();
      }
    },
    onMouseDown: e => {
      // In order to support multiple selection in TileCarousel, disable the text selection using Shift key
      // and to prevent double click text selection when tile can be activated
      if (e.shiftKey || e.detail > 1 && props.onOpen) e.preventDefault();
    },
    children: props.disableInteractiveMode ? props.children : /*#__PURE__*/_jsx(FocusLock, {
      disabled: !props.isInteractive,
      ...props.focusLockProps,
      children: props.children
    })
  });
});
const getTileTokens = (dt, props, isNewTokenValueFlagEnabled) => {
  return {
    bg: dt.tokens['color-container-background-0'],
    'border-radius': dt.tokens['size-border-radius-s'],
    sh: dt.tokens['shadow-depth-s'],
    bd: props.selected ? dt.tokens['color-primary'] : dt.tokens['color-container-border-4'],
    'bd-width': props.selected && dt.themeVariant === 'high-contrast' ? dt.tokens['size-border-width-l'] : dt.tokens['size-border-width-m'],
    'spacing-s': dt.tokens['space-base-s'],
    'outline-radius': dt.tokens['size-border-radius-s'],
    focus: props.variant === 'primary-light' && dt.themeVariant === 'light' ? 'var(--focus-dark-bg)' : 'var(--focus-light-bg)',
    'outline-offset': 'var(--focus-width)',
    ...(!isNewTokenValueFlagEnabled && {
      'outline-offset': 0
    })
  };
};
//# sourceMappingURL=TileContainer.js.map